<template>
    <div class="container">
        <h1 class="my-3">{{ $t('nfc.title') }}</h1>

        <div v-if="!supported" class="alert alert-warning">
            <p class="mb-0">{{ $t('nfc.notSupported') }}</p>
            <p v-if="!secure" class="mb-0 mt-2">{{ $t('nfc.insecureContext') }}</p>
        </div>

        <template v-else>
            <div v-if="message" :class="['alert', `alert-${messageType}`]">{{ message }}</div>

            <div v-if="state === 'idle' || state === 'scanning'" class="d-flex gap-2 mb-3">
                <button v-if="state === 'idle'" class="btn btn-primary" @click="startScan">
                    <i class="fa-solid fa-tag me-2"></i>{{ $t('nfc.start') }}
                </button>
                <button v-else class="btn btn-outline-secondary" @click="stopScan">{{ $t('nfc.stop') }}</button>
            </div>

            <div v-if="state === 'scanning'" class="alert alert-info">{{ $t('nfc.holdTag') }}</div>
            <div v-if="state === 'checking'" class="alert alert-info">{{ $t('nfc.checking') }}</div>

            <!-- Unbound tag: pick the project once, the binding is permanent. -->
            <div v-if="state === 'link'">
                <label class="form-label small">{{ $t('nfc.searchLabel') }}</label>
                <div class="input-group mb-3 rounded-pill">
                    <input type="text" class="form-control" :placeholder="$t('nfc.searchPlaceholder')"
                        v-model="searchTerm" @keyup.enter.prevent="searchProjects" />
                    <span class="input-group-text bg-body" type="button" :title="$t('nfc.search')"
                        @click="searchProjects">
                        <i class="fa-solid fa-magnifying-glass"></i>
                    </span>
                </div>

                <ul v-if="projects.length" class="list-group mb-3">
                    <li v-for="project in projects" :key="project.id" class="list-group-item p-3" role="button"
                        @click="connect(project)">
                        <div class="row align-items-center">
                            <div class="col">
                                <div class="fw-bolder small">{{ project.name }}</div>
                                <div class="small">{{ project.address }}</div>
                            </div>
                            <div class="col-auto text-end">
                                <button class="btn-icon" :disabled="busy"><i class="fa-solid fa-link"></i></button>
                            </div>
                        </div>
                    </li>
                </ul>
                <p v-else-if="searched" class="text-muted">{{ $t('nfc.noResults') }}</p>
            </div>

            <div v-if="state === 'noTasks'" class="card mb-3">
                <div class="card-body">
                    <div class="fw-bolder">{{ projectName }}</div>
                    <div class="text-muted mb-2">{{ $t('nfc.noOpenTasks') }}</div>
                    <router-link v-if="can('Priv_AppProjects')" :to="`/projects/${projectId}`"
                        class="btn btn-outline-primary btn-sm">{{ $t('nfc.openProject') }}</router-link>
                </div>
            </div>

            <button v-if="['link', 'noTasks', 'done'].includes(state)" class="btn btn-primary"
                @click="startScan">
                <i class="fa-solid fa-tag me-2"></i>{{ $t('nfc.scanAgain') }}
            </button>
        </template>
    </div>
</template>

<script>
import axios from '@/services/axios';
import { can as canPerm } from '@/composables/usePerms';
import { clearTaskListFilters, TASK_FILTER_KEYS_STORAGE_KEY } from '@/utils/taskFilters';

// TasksListComponent filter keys that together mean "unfinished maintenance":
// every open stage (each sets finished=0) plus the maintenance priority.
const UNFINISHED_MAINTENANCE_FILTER_KEYS = ['stage0', 'stage1', 'stage2', 'priority1'];

export default {
    name: 'NfcScanView',
    data() {
        return {
            supported: 'NDEFReader' in window,
            secure: window.isSecureContext,
            // idle | scanning | checking | link | noTasks | done
            state: 'idle',
            message: '',
            messageType: 'info',
            abortController: null,
            serial: '',
            searchTerm: '',
            searched: false,
            projects: [],
            busy: false,
            projectId: null,
            projectName: '',
        };
    },
    beforeUnmount() {
        this.abortScan();
    },
    methods: {
        can(perm) {
            return canPerm(perm);
        },
        showMessage(text, type = 'info') {
            this.message = text;
            this.messageType = type;
        },
        apiMessage(error) {
            return error?.response?.data?.error?.message || '';
        },

        async startScan() {
            this.showMessage('');
            this.projects = [];
            this.searchTerm = '';
            this.searched = false;
            this.abortScan();
            this.abortController = new AbortController();
            const reader = new window.NDEFReader();

            reader.onreading = ({ serialNumber }) => {
                // Sent exactly as Web NFC reports it (colon-separated hex), the
                // one format used for both scan and connect.
                if (!serialNumber) {
                    this.showMessage(this.$t('nfc.readFailed'), 'warning');
                    return;
                }
                this.abortScan();
                this.serial = serialNumber;
                this.lookup();
            };
            reader.onreadingerror = () => {
                this.showMessage(this.$t('nfc.readFailed'), 'warning');
            };

            try {
                await reader.scan({ signal: this.abortController.signal });
                this.state = 'scanning';
            } catch (err) {
                this.state = 'idle';
                const key = {
                    NotSupportedError: 'nfc.noHardware',
                    NotReadableError: 'nfc.nfcOff',
                    NotAllowedError: 'nfc.permissionDenied',
                }[err.name];
                if (err.name !== 'AbortError') {
                    this.showMessage(key ? this.$t(key) : `${err.name}: ${err.message}`, 'danger');
                }
            }
        },
        abortScan() {
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
        },
        stopScan() {
            this.abortScan();
            this.state = 'idle';
        },

        async lookup() {
            this.state = 'checking';
            this.showMessage('');
            try {
                const response = await axios.post('nfc/', { serial: this.serial });
                const { project_id, project_name } = response.data.data;
                await this.openProject(project_id, project_name);
            } catch (error) {
                const status = error?.response?.status;
                if (status === 404) {
                    this.state = 'link';
                    if (this.can('Priv_AppProjects')) {
                        this.showMessage(this.$t('nfc.notLinked'), 'warning');
                    } else {
                        // Without AppProjects the project search returns nothing.
                        this.state = 'done';
                        this.showMessage(`${this.$t('nfc.notLinked')} ${this.$t('nfc.askOffice')}`, 'warning');
                    }
                } else {
                    this.failScan(error);
                }
            }
        },
        // Shared by scan and connect, so a closed project reads the same either way.
        failScan(error) {
            const status = error?.response?.status;
            this.state = status === 400 ? 'idle' : 'done';
            if (status === 403) {
                this.showMessage(this.$t('nfc.projectClosed'), 'warning');
            } else if (status === 400) {
                this.showMessage(this.$t('nfc.readFailed'), 'warning');
            } else {
                console.error('NFC lookup failed:', error);
                this.showMessage(this.$t('nfc.error'), 'danger');
            }
        },

        async searchProjects() {
            const term = this.searchTerm.trim();
            if (!term) return;
            try {
                // Only active type-0 projects in the user's own profile group.
                const params = new URLSearchParams({
                    search: term,
                    not_active: '0',
                    type_id: '0',
                    user_profile_group: '1',
                    page: '1',
                    limit: '50',
                });
                const response = await axios.get(`projects/?${params.toString()}`);
                this.projects = response.data.data || [];
                this.searched = true;
            } catch (error) {
                console.error('Error searching projects:', error);
                this.showMessage(this.$t('nfc.error'), 'danger');
            }
        },

        async connect(project) {
            if (this.busy) return;
            if (!window.confirm(this.$t('nfc.confirmLink', { name: project.name }))) return;

            this.busy = true;
            try {
                const response = await axios.post('nfc/connect', { serial: this.serial, project_id: project.id });
                const { project_id, project_name } = response.data.data;
                await this.openProject(project_id, project_name);
            } catch (error) {
                const status = error?.response?.status;
                const apiMessage = this.apiMessage(error);
                if (status === 409 && apiMessage === 'Serial already bound to a project') {
                    // Someone linked this tag meanwhile: carry on with that project.
                    await this.lookup();
                } else if (status === 409) {
                    this.showMessage(this.$t('nfc.alreadyTagged'), 'warning');
                } else if (status === 403 && apiMessage === 'Project is closed') {
                    this.failScan(error);
                } else if (status === 403) {
                    this.showMessage(this.$t('nfc.projectUnavailable'), 'warning');
                } else {
                    this.failScan(error);
                }
            } finally {
                this.busy = false;
            }
        },

        // The API names only the project; where to go is decided from the open
        // maintenance tasks. No `users` filter: the server already limits /tasks
        // to the caller's own unless they hold Priv_AppTasksAll, so `total` is
        // "mine" or "all" exactly as the list it leads to will show.
        async openProject(projectId, projectName) {
            this.projectId = projectId;
            this.projectName = projectName;
            this.state = 'checking';

            const params = new URLSearchParams({
                project_id: projectId,
                priority_id: '1',
                finished: '0',
                sort_by: 'estimated_date',
                sort_order: 'asc',
                page: '1',
                limit: '2',
            });
            let response;
            try {
                response = await axios.get(`tasks/?${params.toString()}`);
            } catch (error) {
                this.failScan(error);
                return;
            }
            const tasks = response.data.data || [];
            const total = Number(response.data.total) || tasks.length;

            if (total === 0) {
                this.state = 'noTasks';
                return;
            }
            if (total === 1 && tasks[0]) {
                this.$router.replace(`/tasks/${tasks[0].id}`);
                return;
            }

            // Sticky list filters would make the list disagree with the count
            // that sent the user there.
            clearTaskListFilters();
            // The project task list is behind Priv_AppProjects; without it fall
            // back to the user's own unfinished list rather than /forbidden.
            if (this.can('Priv_AppTasksAll') && this.can('Priv_AppProjects')) {
                localStorage.setItem(TASK_FILTER_KEYS_STORAGE_KEY, JSON.stringify(UNFINISHED_MAINTENANCE_FILTER_KEYS));
                this.$router.replace({ path: `/projects/${projectId}/tasks`, query: { page: '1' } });
            } else {
                this.$router.replace({ path: '/tasks-unfinished', query: { project_id: String(projectId), priority_id: '1' } });
            }
        },
    },
};
</script>
